import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiService from '../services/ApiService';
import { extractTextFromPdf, isPdfFileName } from '../utils/pdfTextExtract';
import { extractTextFromExcel, isExcelFileName } from '../utils/excelImportExtract';
import { IMPORT_FORMAT_EXAMPLES, type ImportFormatExampleKey } from '../data/importFormatExamples';
import { ImportReviewStep } from './import/ImportReviewStep';
import { ImportInputStep } from './import/ImportInputStep';
import { useModalFocusTrap } from '../hooks/useModalFocusTrap';
import { acquireBodyScrollLock } from '../utils/bodyScrollLock';
import { ImportStepIndicator } from './import/ImportStepIndicator';
import type { PendingCategoryApply } from './import/ImportReviewGroupedList';
import type { ParsedModification } from './import/importReviewTypes';
import {
  IMPORT_ALLOWED_EXTENSIONS,
  IMPORT_MAX_FILE_BYTES,
  type ImportHistorySession,
  type SmartSuggestionFields,
} from './import/importConstants';
import {
  isFormFieldTarget,
  isPersistentSession,
  reindexIndexSet,
  reindexPendingCategoryApply,
  reindexRecord,
  sanitizeDisplayFileName,
  truncateForAnalyze,
  looksLikeBuildListPaste,
  parseBuildListClientSide,
  formatSessionStatus,
} from './import/importModalUtils';
import { findSimilarCategoryCandidates } from '../utils/importIntelligence';
import { markAiSuggestedFields } from '../utils/aiSuggestionFields';

interface ImportModificationsModalProps {
  vehicleId: number;
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  vehicleInfo?: {
    make: string;
    model: string;
    year: number;
  };
}

type ImportStep = 'input' | 'review' | 'importing' | 'complete';

export const ImportModificationsModal: React.FC<ImportModificationsModalProps> = ({
  vehicleId,
  isOpen,
  onClose,
  onImportComplete,
  vehicleInfo
}) => {
  const [currentStep, setCurrentStep] = useState<ImportStep>('input');
  const [inputText, setInputText] = useState('');
  const [importType, setImportType] = useState<'text' | 'file'>('text');
  const [dragOver, setDragOver] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [parsedModifications, setParsedModifications] = useState<ParsedModification[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableVendors, setAvailableVendors] = useState<string[]>([]);
  const [importSessionId, setImportSessionId] = useState<string>('fallback_session');
  const [preferDeeperCleanup, setPreferDeeperCleanup] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [uploadedFileSize, setUploadedFileSize] = useState<number | null>(null);
  const [organizeStatus, setOrganizeStatus] = useState('');
  const [dirty, setDirty] = useState(false);
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [canUndoImport, setCanUndoImport] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [parseMode, setParseMode] = useState<'standard' | 'receipt'>('standard');
  const [receiptConfirmed, setReceiptConfirmed] = useState(false);
  const [importHistory, setImportHistory] = useState<ImportHistorySession[]>([]);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [isExtractingFile, setIsExtractingFile] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [excelExtractInfo, setExcelExtractInfo] = useState<{
    rowCount: number;
    format: string;
    sheetName: string;
  } | null>(null);
  const [bulkEditMode, setBulkEditMode] = useState<'category' | 'vendor' | 'cost' | null>(null);
  const [selectedMods, setSelectedMods] = useState<Set<number>>(new Set());
  const [smartSuggestions, setSmartSuggestions] = useState<Record<number, SmartSuggestionFields>>({});
  const [pendingCategoryApply, setPendingCategoryApply] = useState<PendingCategoryApply | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [selectMode, setSelectMode] = useState(false);

  useModalFocusTrap(isOpen, panelRef);

  // Load available categories and vendors on mount
  useEffect(() => {
    if (isOpen) {
      loadCategoriesAndVendors();
      loadImportHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when modal opens
  }, [isOpen, vehicleId]);

  const loadImportHistory = async () => {
    try {
      const sessions = await apiService.getImportHistory(vehicleId, 8);
      setImportHistory(sessions);
    } catch {
      setImportHistory([]);
    }
  };

  const loadCategoriesAndVendors = async () => {
    try {
      const [categories, vendors] = await Promise.all([
        apiService.getCategories(),
        apiService.getVendors(),
      ]);

      if (categories.length > 0) {
        setAvailableCategories(categories.map((c) => c.name));
      } else {
        setAvailableCategories([
          'Engine', 'Exhaust', 'Intake', 'Suspension', 'Brakes', 'Wheels', 'Tires',
          'Interior', 'Exterior', 'Electronics', 'Turbo/Supercharger', 'Fuel System',
          'Transmission', 'Drivetrain', 'Cooling', 'Ignition', 'Maintenance', 'Audio',
          'Performance', 'Cosmetic', 'Safety', 'Lighting', 'Miscellaneous'
        ]);
      }

      if (vendors.length > 0) {
        setAvailableVendors(vendors.map((v) => v.name).filter(Boolean));
      } else {
        setAvailableVendors([
          'K&N', 'Injen', 'AEM', 'HKS', 'Garrett', 'Mishimoto', 'BC Racing', 'Eibach',
          'Bilstein', 'KW', 'Stoptech', 'Brembo', 'Enkei', 'Work', 'BBS', 'Recaro',
          'Sparco', 'OMP', 'Cobb', 'Perrin', 'Invidia', 'Borla', 'Magnaflow', 'Skunk2',
          'Hondata', 'AEM Electronics', 'Walbro', 'DeatschWerks', 'Injector Dynamics'
        ]);
      }
    } catch (error) {
      console.error('Failed to load categories/vendors:', error);
    }
  };

  const generateSmartSuggestions = useCallback((modification: ParsedModification): SmartSuggestionFields => {
    const suggestions: SmartSuggestionFields = {};
    
    // Cost suggestions based on category and title
    if (!modification.cost || modification.cost === 0) {
      suggestions.cost = getCostEstimate(modification.title, modification.category);
    }
    
    // Vendor suggestions based on title keywords
    if (!modification.vendor) {
      const titleLower = modification.title.toLowerCase();
      const suggestedVendor = availableVendors.find(vendor => 
        titleLower.includes(vendor.toLowerCase()) ||
        vendor.toLowerCase().includes(titleLower.split(' ')[0])
      );
      if (suggestedVendor) {
        suggestions.vendor = suggestedVendor;
      }
    }
    
    // Category suggestions based on keywords
    const titleLower = modification.title.toLowerCase();
    const categoryKeywords = {
      'Engine': ['engine', 'motor', 'block', 'head', 'cam', 'valve', 'piston'],
      'Exhaust': ['exhaust', 'catback', 'downpipe', 'header', 'manifold', 'muffler'],
      'Intake': ['intake', 'air filter', 'cold air', 'ram air', 'airbox'],
      'Suspension': ['coilover', 'spring', 'shock', 'strut', 'suspension', 'damper'],
      'Brakes': ['brake', 'pad', 'rotor', 'disc', 'caliper', 'line'],
      'Turbo/Supercharger': ['turbo', 'supercharger', 'intercooler', 'wastegate', 'bov'],
      'Wheels': ['wheel', 'rim', 'alloy'],
      'Tires': ['tire', 'tyre', 'rubber']
    };
    
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some(keyword => titleLower.includes(keyword))) {
        if (modification.category === 'Miscellaneous' || modification.confidence < 0.7) {
          suggestions.category = category;
        }
        break;
      }
    }
    
    return suggestions;
  }, [availableVendors]);

  // Update smart suggestions when modifications change
  useEffect(() => {
    const newSuggestions: Record<number, SmartSuggestionFields> = {};
    parsedModifications.forEach((mod, index) => {
      newSuggestions[index] = generateSmartSuggestions(mod);
    });
    setSmartSuggestions(newSuggestions);
  }, [parsedModifications, generateSmartSuggestions]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!isOpen) return undefined;
    return acquireBodyScrollLock();
  }, [isOpen]);

  // Keyboard shortcuts and Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape' && !isImporting && !isAnalyzing) {
        e.preventDefault();
        requestClose();
        return;
      }

      if (currentStep !== 'review' || isFormFieldTarget(e.target)) return;
      
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'a':
            e.preventDefault();
            setSelectedMods(new Set(parsedModifications.map((_, i) => i)));
            break;
          case 'h':
            e.preventDefault();
            setSelectMode(true);
            selectAllHighConfidence();
            break;
          case 's':
            e.preventDefault();
            applyAllSuggestions();
            break;
        }
      } else if (e.key === 'Delete' && selectedMods.size > 0) {
        e.preventDefault();
        removeSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyboard shortcuts bound to current step state
  }, [isOpen, currentStep, parsedModifications, selectedMods, isImporting, isAnalyzing]);

  if (!isOpen) return null;

  const resetModal = () => {
    setCurrentStep('input');
    setInputText('');
    setImportType('text');
    setParsedModifications([]);
    setIsAnalyzing(false);
    setIsImporting(false);
    setImportedCount(0);
    setErrors([]);
    setSelectedMods(new Set());
    setImportSessionId('fallback_session');
    setPreferDeeperCleanup(false);
    setShowAdvancedOptions(false);
    setUploadedFileSize(null);
    setOrganizeStatus('');
    setDirty(false);
    setSelectMode(false);
    setExcludedIndices(new Set());
    setCollapsedCategories(new Set());
    setCollapsedGroups(new Set());
    setPendingCategoryApply(null);
    setCanUndoImport(false);
    setIsUndoing(false);
    setParseMode('standard');
    setReceiptConfirmed(false);
    setShowImportHistory(false);
    setIsExtractingFile(false);
    setUploadedFileName(null);
    setExcelExtractInfo(null);
    setSmartSuggestions({});
  };

  const hasDirtyWork = () => {
    if (dirty) return true;
    if (inputText.trim().length > 0) return true;
    if (uploadedFileName) return true;
    if (currentStep === 'review' && parsedModifications.length > 0) return true;
    return false;
  };

  const requestClose = () => {
    if (isImporting || isAnalyzing) return;
    if (hasDirtyWork() && currentStep !== 'complete') {
      const discard = window.confirm(
        'Leave this import?\n\nYour current review will be lost. Completed imports can still be undone from Done when a saved session exists.'
      );
      if (!discard) return;
    }
    handleClose();
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

     const handleBackdropClick = (e: React.MouseEvent) => {
     if (e.target === e.currentTarget && !isImporting && !isAnalyzing) {
       requestClose();
     }
   };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileExtension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');

    if (!IMPORT_ALLOWED_EXTENSIONS.includes(fileExtension as typeof IMPORT_ALLOWED_EXTENSIONS[number])) {
      setErrors(['We can’t read that file type. Try Excel, CSV, PDF, or a text file.']);
      return;
    }

    if (file.size > IMPORT_MAX_FILE_BYTES) {
      setErrors(['This file is over 10 MB. Export a smaller sheet or split the list.']);
      return;
    }

    const safeName = sanitizeDisplayFileName(file.name);

    setErrors([]);
    setExcelExtractInfo(null);
    setUploadedFileSize(file.size);
    setDirty(true);

    try {
      if (isPdfFileName(file.name)) {
        setIsExtractingFile(true);
        const text = await extractTextFromPdf(file);
        setInputText(text);
        setImportType('file');
        setUploadedFileName(safeName);
      } else if (isExcelFileName(file.name)) {
        setIsExtractingFile(true);
        const result = await extractTextFromExcel(file);
        setInputText(result.text);
        setImportType('file');
        setUploadedFileName(safeName);
        setExcelExtractInfo({
          rowCount: result.rowCount,
          format: result.format,
          sheetName: result.sheetName,
        });
      } else {
        const text = await file.text();
        setInputText(text);
        setImportType('file');
        setUploadedFileName(safeName);
      }
    } catch (error: any) {
      setErrors([error.message || 'Failed to read file']);
      setUploadedFileName(null);
      setExcelExtractInfo(null);
    } finally {
      setIsExtractingFile(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleAnalyze = async () => {
    if (!inputText.trim()) {
      setErrors(['Paste a list or upload a file to continue.']);
      return;
    }

    setIsAnalyzing(true);
    setErrors([]);
    setOrganizeStatus('Finding sections');
    const statusTimer = window.setInterval(() => {
      setOrganizeStatus((prev) => {
        const cycle = [
          'Finding sections',
          'Grouping parts',
          'Reading brands and specifications',
          'Checking for duplicates',
        ];
        const i = cycle.indexOf(prev);
        return cycle[(i + 1) % cycle.length];
      });
    }, 2200);

    const { text: analyzeText, truncated } = truncateForAnalyze(inputText);

    const runAnalyze = async (rulesOnly: boolean) => {
      const structuredImport =
        excelExtractInfo?.format === 'structured_json' || inputText.trim().startsWith('{"modifications"');
      return apiService.analyzeModificationText(vehicleId, analyzeText, importType, {
        useRulesOnly: rulesOnly || structuredImport,
        structuredImport,
      });
    };

    try {
      // Structured build lists must use deterministic parse — AI times out on IONOS for long sheets.
      // Auto: structured/build lists use deterministic organize; messy notes use deeper cleanup when Advanced is on or list doesn't look structured.
      const structuredImportFlag =
        excelExtractInfo?.format === 'structured_json' || inputText.trim().startsWith('{"modifications"');
      const preferRules =
        structuredImportFlag ||
        (!preferDeeperCleanup && looksLikeBuildListPaste(analyzeText));

      let data;
      try {
        data = await runAnalyze(preferRules);
      } catch (firstError: any) {
        // Auto-retry once with rules-only when AI/analyze path fails on a build list.
        if (!preferRules || !looksLikeBuildListPaste(analyzeText)) {
          if (looksLikeBuildListPaste(analyzeText)) {
            data = await runAnalyze(true);
          } else {
            throw firstError;
          }
        } else {
          // Already rules-only; one more attempt in case of transient failure.
          try {
            data = await runAnalyze(true);
          } catch {
            throw firstError;
          }
        }
      }

      let mods = (data.modifications || []).map((mod: ParsedModification) => {
        const marked = markAiSuggestedFields(mod, { force: true });
        // Keep dropdown values on the canonical list; preserve unknown as suggestion.
        if (
          marked.category &&
          availableCategories.length > 0 &&
          !availableCategories.includes(marked.category)
        ) {
          return {
            ...marked,
            category_suggestion: marked.category,
            category: availableCategories.includes('Miscellaneous')
              ? 'Miscellaneous'
              : availableCategories[0],
            category_is_new: false,
            category_confirmed: true,
            ai_suggested: {
              ...marked.ai_suggested,
              category: true,
            },
          };
        }
        return {
          ...marked,
          category_is_new: false,
          category_confirmed: true,
        };
      });

      // If the API fails open with an empty list, or throws below, try local parse.
      if (mods.length === 0 && looksLikeBuildListPaste(analyzeText)) {
        mods = parseBuildListClientSide(analyzeText).map((mod) =>
          markAiSuggestedFields(mod, { force: true })
        );
      }

      if (mods.length === 0) {
        setErrors([
          'No parts were found in that list. Check the formatting, or try a shorter section.',
        ]);
        return;
      }

      setParsedModifications(mods);
      setImportSessionId(data.session_token || 'fallback_session');
      setParseMode(data.parse_mode === 'receipt' ? 'receipt' : 'standard');
      setReceiptConfirmed(false);
      setExcludedIndices(new Set());
      // Large imports: keep sections collapsed for performance
      if (mods.length >= 80) {
        const cats = new Set<string>(
          mods.map((m: ParsedModification) => m.category || 'Miscellaneous')
        );
        setCollapsedCategories(cats);
      } else {
        setCollapsedCategories(new Set());
      }
      setCollapsedGroups(new Set());
      setPendingCategoryApply(null);
      setCurrentStep('review');
      if (truncated) {
        setErrors([
          'Your list is very long. Bilt:Spec organized the first portion so the request stays reliable. Split into another import if anything is missing.',
        ]);
      }
    } catch (error: any) {
      // Last resort: organize locally so a backend/AI failure does not block paste import.
      if (looksLikeBuildListPaste(analyzeText)) {
        const localMods = parseBuildListClientSide(analyzeText).map((mod) =>
          markAiSuggestedFields(mod, { force: true })
        );
        if (localMods.length > 0) {
          setParsedModifications(localMods);
          setImportSessionId('fallback_session');
          setParseMode('standard');
          setReceiptConfirmed(false);
          setExcludedIndices(new Set());
          setCollapsedCategories(new Set());
          setCollapsedGroups(new Set());
          setPendingCategoryApply(null);
          setCurrentStep('review');
          setErrors([
            'We could not finish organizing online, so this list was organized on your device. Your list is still here. Review carefully before adding.',
          ]);
          return;
        }
      }
      setErrors([
        error.message ||
          'We could not finish organizing this list. Your list is still here. Try again.',
      ]);
      console.error('Import organize failed:', error);
    } finally {
      window.clearInterval(statusTimer);
      setIsAnalyzing(false);
      setOrganizeStatus('');
    }
  };

  const handleModificationEdit = (index: number, field: keyof ParsedModification, value: any) => {
    setDirty(true);
    setParsedModifications(prev => prev.map((mod, i) => 
      i === index ? { ...mod, [field]: value } : mod
    ));
  };

  const handleCategoryChange = (index: number, newCategory: string, previousCategory: string) => {
    const similarIndices = findSimilarCategoryCandidates(
      parsedModifications,
      index,
      previousCategory,
      newCategory
    );
    if (similarIndices.length > 0) {
      setPendingCategoryApply({
        changedIndex: index,
        newCategory,
        similarIndices,
      });
    } else {
      setPendingCategoryApply(null);
    }
  };

  const handleApplyCategoryToSimilar = (
    changedIndex: number,
    newCategory: string,
    similarIndices: number[]
  ) => {
    const allIndices = new Set([changedIndex, ...similarIndices]);
    setParsedModifications((prev) =>
      prev.map((mod, i) =>
        allIndices.has(i)
          ? { ...mod, category: newCategory, category_confirmed: true }
          : mod
      )
    );
    setPendingCategoryApply(null);
  };

  const handleImport = async (options?: { highConfidenceOnly?: boolean }) => {
    let approvedMods = parsedModifications.filter(
      (mod, index) => mod.title.trim() && !excludedIndices.has(index)
    );

    if (options?.highConfidenceOnly) {
      approvedMods = approvedMods.filter((mod) => mod.confidence >= 0.8);
    }
    
    if (approvedMods.length === 0) {
      setErrors([options?.highConfidenceOnly
        ? 'No ready parts to add with that filter. Include more parts or review items that need a look.'
        : 'Include at least one part to add to your build.']);
      return;
    }

    const unconfirmedCategories = approvedMods.filter(
      (mod) => mod.category_is_new && !mod.category_confirmed
    );
    if (unconfirmedCategories.length > 0) {
      setErrors([
        `${unconfirmedCategories.length} item(s) use a new category. Confirm each or pick an existing category before importing.`,
      ]);
      return;
    }

    if (parseMode === 'receipt' && !receiptConfirmed) {
      setErrors(['Receipt import detected. Confirm you have reviewed every line item before importing.']);
      return;
    }

    setCurrentStep('importing');
    setIsImporting(true);
    setImportedCount(0);

    try {
      const result = await apiService.importModificationsBatch(vehicleId, approvedMods, importSessionId);

      const count = result.imported_count ?? 0;
      if (count > 0) {
        setImportedCount(count);
        if (result.errors?.length) {
          setErrors(result.errors);
        }
        setCanUndoImport(isPersistentSession(importSessionId));
        setDirty(false);
        setCurrentStep('complete');
      } else {
        setErrors(result.errors?.length ? result.errors : ['We could not add those parts. Nothing was saved. Try again.']);
        setCurrentStep('review');
      }
    } catch (error: any) {
      setErrors([error.message || 'We could not add those parts. Your review is still here. Try again.']);
      setCurrentStep('review');
    } finally {
      setIsImporting(false);
    }
  };

  const handleUndoImport = async () => {
    if (!canUndoImport || !isPersistentSession(importSessionId)) {
      return;
    }

    setIsUndoing(true);
    setErrors([]);

    try {
      const result = await apiService.undoImportBatch(vehicleId, importSessionId);
      setCanUndoImport(false);
      setImportedCount(Math.max(0, importedCount - (result.deleted_count ?? 0)));
      onImportComplete();
      handleClose();
    } catch (error: any) {
      setErrors([error.message || 'Failed to undo import. Please try again.']);
    } finally {
      setIsUndoing(false);
    }
  };

  const handleImportComplete = () => {
    onImportComplete();
    handleClose();
  };

  const loadFormatExample = (key: ImportFormatExampleKey) => {
    setInputText(IMPORT_FORMAT_EXAMPLES[key]);
    setUploadedFileName(null);
    setExcelExtractInfo(null);
    setImportType('text');
    setErrors([]);
  };

  const skipDuplicateRows = () => {
    setExcludedIndices((prev) => {
      const next = new Set(prev);
      parsedModifications.forEach((mod, index) => {
        if (mod.duplicate_warning) {
          next.add(index);
        }
      });
      return next;
    });
  };

  const toggleIncluded = (index: number) => {
    setDirty(true);
    setExcludedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleCategoryCollapsed = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const toggleGroupCollapsed = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removeModificationAt = (index: number) => {
    setParsedModifications((prev) => prev.filter((_, i) => i !== index));
    setExcludedIndices((prev) => reindexIndexSet(prev, index));
    setSelectedMods((prev) => reindexIndexSet(prev, index));
    setSmartSuggestions((prev) => reindexRecord(prev, index));
    setPendingCategoryApply((prev) => reindexPendingCategoryApply(prev, index));
  };

  const getIncludedCount = () =>
    parsedModifications.filter((mod, i) => mod.title.trim() && !excludedIndices.has(i)).length;

  const importBlockers = (): string[] => {
    const blockers: string[] = [];
    const titled = parsedModifications.filter(
      (mod, index) => mod.title.trim() && !excludedIndices.has(index)
    );
    if (titled.length === 0) {
      blockers.push('Include at least one part with a name.');
    }
    const unconfirmed = titled.filter((mod) => mod.category_is_new && !mod.category_confirmed);
    if (unconfirmed.length > 0) {
      blockers.push(`${unconfirmed.length} new categor${unconfirmed.length === 1 ? 'y' : 'ies'} need confirmation.`);
    }
    if (parseMode === 'receipt' && !receiptConfirmed) {
      blockers.push('Confirm you reviewed receipt line items.');
    }
    return blockers;
  };

  const acceptAttr = IMPORT_ALLOWED_EXTENSIONS.join(',');

  const clearUploadedFile = () => {
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setExcelExtractInfo(null);
    setImportType('text');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const renderInputStep = () => (
    <>
    <ImportInputStep
      inputText={inputText}
      onInputTextChange={(value) => {
        setInputText(value);
        setDirty(true);
        setImportType('text');
      }}
      dragOver={dragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onFileClick={handleFileClick}
      fileInputRef={fileInputRef}
      onFileChange={handleFileSelect}
      acceptAttr={acceptAttr}
      isExtractingFile={isExtractingFile}
      uploadedFileName={uploadedFileName}
      uploadedFileSize={uploadedFileSize}
      excelExtractInfo={excelExtractInfo}
      onClearFile={clearUploadedFile}
      preferDeeperCleanup={preferDeeperCleanup}
      onPreferDeeperCleanupChange={setPreferDeeperCleanup}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvanced={() => setShowAdvancedOptions((v) => !v)}
      onLoadExample={(key) => {
        loadFormatExample(key);
        setDirty(true);
      }}
      onTryPrimaryExample={() => {
        loadFormatExample('simpleList');
        setDirty(true);
      }}
    />
    {importHistory.length > 0 && (
      <div className="mt-4 rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 px-3 py-3">
        <button
          type="button"
          onClick={() => setShowImportHistory((v) => !v)}
          className="w-full flex items-center justify-between text-left text-xs text-gray-400 hover:text-gray-200 min-h-[40px]"
          aria-expanded={showImportHistory}
        >
          <span>Recent imports on this build</span>
          <span>{showImportHistory ? 'Hide' : 'Show'}</span>
        </button>
        {showImportHistory && (
          <ul className="mt-2 space-y-2">
            {importHistory.slice(0, 5).map((session) => (
              <li key={session.session_token} className="text-xs text-gray-500 flex justify-between gap-2">
                <span>
                  {session.imported_modifications}/{session.total_modifications} parts ·{' '}
                  {formatSessionStatus(session.status)}
                  {session.can_undo ? ' · can undo' : ''}
                </span>
                {session.created_at ? (
                  <span className="shrink-0 text-gray-600">
                    {new Date(session.created_at).toLocaleDateString()}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-gray-600 mt-2">
          Mid-review drafts are not resumed yet. After a successful import, Undo is available on the Done step when the session was saved.
        </p>
      </div>
    )}
    </>
  );

  const renderReviewStep = () => (
    <ImportReviewStep
      parsedModifications={parsedModifications}
      excludedIndices={excludedIndices}
      collapsedCategories={collapsedCategories}
      collapsedGroups={collapsedGroups}
      parseMode={parseMode}
      receiptConfirmed={receiptConfirmed}
      setReceiptConfirmed={setReceiptConfirmed}
      availableCategories={availableCategories}
      availableVendors={availableVendors}
      smartSuggestions={smartSuggestions}
      selectedMods={selectedMods}
      selectMode={selectMode}
      setSelectMode={setSelectMode}
      bulkEditMode={bulkEditMode}
      setBulkEditMode={setBulkEditMode}
      setSelectedMods={setSelectedMods}
      setCollapsedCategories={setCollapsedCategories}
      setCollapsedGroups={setCollapsedGroups}
      setParsedModifications={setParsedModifications}
      pendingCategoryApply={pendingCategoryApply}
      onCategoryChange={handleCategoryChange}
      onApplyCategoryToSimilar={handleApplyCategoryToSimilar}
      onDismissCategoryApply={() => setPendingCategoryApply(null)}
      errors={errors}
      onToggleCategory={toggleCategoryCollapsed}
      onToggleGroup={toggleGroupCollapsed}
      onToggleIncluded={toggleIncluded}
      onEdit={handleModificationEdit}
      onRemove={removeModificationAt}
      onSkipDuplicates={skipDuplicateRows}
      onApplySuggestions={applyAllSuggestions}
      onEstimateCosts={() => {
        setParsedModifications((prev) =>
          prev.map((mod) => ({
            ...mod,
            cost: !mod.cost || mod.cost === 0 ? getCostEstimate(mod.title, mod.category) : mod.cost,
          }))
        );
      }}
      getIncludedCount={getIncludedCount}
    />
  );

  const renderAnalyzingOverlay = () => (
    <div className="text-center py-16 px-4" aria-live="polite" aria-busy="true">
      <div className="w-14 h-14 bg-[#141414] border border-white/[0.08] rounded-full flex items-center justify-center mx-auto mb-5">
        <div className="animate-spin w-7 h-7 border-2 border-orange-400 border-t-transparent rounded-full" />
      </div>
      <h3 className="heading-section mb-2">Organizing your build list</h3>
      <p className="text-muted text-sm mb-2">{organizeStatus || 'Finding sections'}</p>
      <p className="text-muted text-sm max-w-sm mx-auto opacity-80">
        {uploadedFileName ? `Working on ${uploadedFileName}. ` : ''}
        Large lists can take a minute. Your list stays here if something goes wrong.
      </p>
    </div>
  );

  const renderImportingStep = () => (
    <div className="text-center py-12" aria-live="polite" aria-busy="true">
      <div className="mb-6">
        <div className="w-16 h-16 bg-[#141414] border border-white/[0.08] rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="animate-spin w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full" />
        </div>
        <h3 className="heading-section mb-2">Adding to your build…</h3>
        <p className="text-muted">
          Saving {getIncludedCount()} part{getIncludedCount() === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="text-center py-12 space-y-6" aria-live="polite">
      <div>
        <div className="w-16 h-16 bg-[#141414] border border-white/[0.08] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="heading-section mb-2">Your build list has been added</h3>
        <p className="text-muted">
          {importedCount} part{importedCount === 1 ? '' : 's'} {importedCount === 1 ? 'is' : 'are'} on this build.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 justify-center max-w-md mx-auto">
        <button type="button" onClick={handleClose} className="btn-primary min-h-[44px]">
          View build
        </button>
        <button
          type="button"
          onClick={() => {
            resetModal();
            setCurrentStep('input');
          }}
          className="btn-secondary min-h-[44px]"
        >
          Import another list
        </button>
      </div>

      {canUndoImport ? (
        <p className="text-gray-500 text-xs">
          Made a mistake?{' '}
          <button
            type="button"
            onClick={handleUndoImport}
            disabled={isUndoing}
            className="text-orange-400 hover:text-orange-300 underline"
          >
            {isUndoing ? 'Undoing…' : 'Undo this import'}
          </button>
        </p>
      ) : (
        <p className="text-gray-600 text-xs max-w-sm mx-auto">
          Undo isn’t available for this import. If something looks wrong, edit or remove parts on the build.
        </p>
      )}
    </div>
  );

  const renderStepContent = () => {
    if (isAnalyzing && currentStep === 'input') {
      return renderAnalyzingOverlay();
    }
    switch (currentStep) {
      case 'input': return renderInputStep();
      case 'review': return renderReviewStep();
      case 'importing': return renderImportingStep();
      case 'complete': return renderCompleteStep();
      default: return renderInputStep();
    }
  };

  const renderFooter = () => {
    const readyToImport = getIncludedCount();
    const needsReview = parsedModifications.filter(
      (mod, index) =>
        !excludedIndices.has(index) &&
        (mod.confidence < 0.6 ||
          mod.category === 'Miscellaneous' ||
          (Boolean(mod.category_is_new) && !mod.category_confirmed) ||
          Boolean(mod.duplicate_warning))
    ).length;
    const blockers = importBlockers();

    return (
      <div className="sticky bottom-0 bg-[#0c0c0c] border-t border-white/[0.08] px-4 sm:px-6 py-3 sm:py-4 shrink-0">
        {currentStep === 'input' && (
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              type="button"
              onClick={requestClose}
              className="btn-secondary min-h-[44px] w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={!inputText.trim() || isAnalyzing || isExtractingFile}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto"
            >
              {isAnalyzing ? 'Organizing…' : 'Organize my list'}
            </button>
          </div>
        )}

        {currentStep === 'review' && (
          <div className="space-y-3">
            <div className="text-xs text-gray-400 space-y-0.5">
              <p>
                <span className="text-white font-medium">{readyToImport}</span> parts will be added
                {excludedIndices.size > 0 ? (
                  <> · <span className="text-gray-300">{excludedIndices.size}</span> excluded</>
                ) : null}
              </p>
              {needsReview > 0 && (
                <p className="text-amber-300/90 flex items-center gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {needsReview} item{needsReview === 1 ? '' : 's'} still need a look. You can still add the rest
                </p>
              )}
              {selectMode && selectedMods.size > 0 && (
                <div className="flex items-center gap-2 text-orange-400 pt-1">
                  <span>{selectedMods.size} selected</span>
                  <button
                    type="button"
                    onClick={removeSelected}
                    className="text-red-400 hover:text-red-300 text-xs px-2 py-1.5 border border-red-600 rounded hover:bg-red-900/20 min-h-[36px]"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                onClick={() => setCurrentStep('input')}
                className="btn-secondary min-h-[44px] w-full sm:w-auto"
              >
                Back
              </button>

              <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
                {blockers.length > 0 && (
                  <p className="text-xs text-amber-300/90 text-center sm:text-right sm:max-w-md">
                    {blockers.join(' ')}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => handleImport()}
                  disabled={blockers.length > 0 || isImporting}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto px-5"
                >
                  {isImporting
                    ? 'Adding…'
                    : `Add ${readyToImport} part${readyToImport === 1 ? '' : 's'} to this build`}
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'importing' && (
          <div className="flex items-center justify-center gap-3 text-sm text-gray-400" aria-live="polite">
            <div className="animate-spin w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full" />
            <span>Adding parts to your build…</span>
          </div>
        )}

        {currentStep === 'complete' && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleImportComplete}
              className="btn-primary min-h-[44px] w-full sm:w-auto"
            >
              View build
            </button>
          </div>
        )}
      </div>
    );
  };

  // Cost estimation database
  const getCostEstimate = (title: string, category: string): number => {
    const titleLower = title.toLowerCase();
    
    // Common part cost estimates
    const costEstimates: { [key: string]: number } = {
      // Engine
      'cold air intake': 300,
      'intake': 300,
      'air filter': 50,
      'throttle body': 400,
      'fuel injectors': 600,
      'fuel pump': 200,
      
      // Turbo
      'turbo': 2500,
      'turbocharger': 2500,
      'intercooler': 400,
      'blow off valve': 150,
      'wastegate': 300,
      'piping': 200,
      
      // Exhaust  
      'exhaust': 600,
      'catback': 700,
      'downpipe': 350,
      'headers': 500,
      'muffler': 300,
      'resonator': 150,
      
      // Suspension
      'coilovers': 1200,
      'springs': 250,
      'shocks': 400,
      'struts': 400,
      'sway bar': 200,
      'bushings': 100,
      
      // Brakes
      'brake pads': 150,
      'brake pad': 150,
      'rotors': 300,
      'calipers': 600,
      'brake lines': 120,
      
      // Wheels
      'wheels': 1200,
      'wheel': 300,
      'tires': 800,
      'tire': 200,
      
      // Electronics
      'ecu': 600,
      'wideband': 250,
      'gauges': 150,
      'sensors': 100,
    };
    
    // Check for matches in title
    for (const [part, cost] of Object.entries(costEstimates)) {
      if (titleLower.includes(part)) {
        return cost;
      }
    }
    
    // Category-based fallbacks
    const categoryEstimates: { [key: string]: number } = {
      'Engine': 400,
      'Turbo': 800,
      'Exhaust': 500,
      'Suspension': 600,
      'Brakes': 250,
      'Wheels': 800,
      'Electronics': 300,
      'Interior': 200,
      'Exterior': 400,
      'Drivetrain': 500,
      'Cooling': 300,
    };
    
    return categoryEstimates[category] || 200;
  };

  const applyAllSuggestions = () => {
    setParsedModifications(prev => prev.map((mod, index) => {
      const suggestions = smartSuggestions[index] || {};
      return {
        ...mod,
        vendor: !mod.vendor && suggestions.vendor ? suggestions.vendor : mod.vendor,
        category: mod.category === 'Miscellaneous' && suggestions.category ? suggestions.category : mod.category,
        cost: (!mod.cost || mod.cost === 0) && suggestions.cost ? suggestions.cost : mod.cost
      };
    }));
  };

  const selectAllHighConfidence = () => {
    const highConfidenceIndices = parsedModifications
      .map((mod, index) => ({ mod, index }))
      .filter(({ mod }) => mod.confidence >= 0.8)
      .map(({ index }) => index);
    setSelectedMods(new Set(highConfidenceIndices));
  };

  const removeSelected = () => {
    const toRemove = selectedMods;
    setParsedModifications((prev) => prev.filter((_, index) => !toRemove.has(index)));
    setExcludedIndices((prev) => {
      const next = new Set<number>();
      let newIndex = 0;
      for (let oldIndex = 0; oldIndex < parsedModifications.length; oldIndex++) {
        if (!toRemove.has(oldIndex)) {
          if (prev.has(oldIndex)) next.add(newIndex);
          newIndex++;
        }
      }
      return next;
    });
    setSmartSuggestions((prev) => {
      const next: Record<number, SmartSuggestionFields> = {};
      let newIndex = 0;
      for (let oldIndex = 0; oldIndex < parsedModifications.length; oldIndex++) {
        if (!toRemove.has(oldIndex)) {
          if (prev[oldIndex]) next[newIndex] = prev[oldIndex];
          newIndex++;
        }
      }
      return next;
    });
    setPendingCategoryApply((prev) => {
      if (!prev) return null;
      let next: PendingCategoryApply | null = prev;
      Array.from(toRemove)
        .sort((a, b) => b - a)
        .forEach((idx) => {
          next = reindexPendingCategoryApply(next, idx);
        });
      return next;
    });
    setSelectedMods(new Set());
  };

  return (
    <div
      className="modal-overlay-sheet"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-modal-title"
    >
      <div
        ref={panelRef}
        className="modal-sheet w-full max-w-[390px] sm:max-w-4xl max-h-[94vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-white/[0.08] shrink-0">
          <div className="min-w-0">
             <p className="sm:hidden text-center text-gray-600 text-xs mb-2" aria-hidden>───</p>
             <h2 id="import-modal-title" className="heading-card">
               Import build list
             </h2>
             <p className="text-muted text-sm mt-1 hidden sm:block">
               Paste or upload the list you already have. Review before anything is saved.
             </p>
           </div>
          <button
             type="button"
             onClick={requestClose}
             className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/[0.06] min-w-[44px] min-h-[44px] flex items-center justify-center"
             disabled={isImporting || isAnalyzing}
             aria-label="Close import"
           >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-white/[0.08]">
          <ImportStepIndicator currentStep={currentStep} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {currentStep === 'input' && !isAnalyzing && errors.length > 0 && (
            <div className="mb-4 rounded-xl border border-red-700/60 bg-red-950/30 p-4" role="alert" aria-live="assertive">
              <p className="text-red-300 text-sm font-medium mb-1">We could not finish organizing this list</p>
              <ul className="text-red-200/90 text-sm space-y-1 mb-3">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mb-3">
                Your list is still here. Try again, use a smaller batch, or adjust the text and organize again.
              </p>
              <button
                type="button"
                onClick={handleAnalyze}
                disabled={!inputText.trim() || isAnalyzing || isExtractingFile}
                className="btn-primary min-h-[44px] disabled:opacity-50"
              >
                Try again
              </button>
            </div>
          )}
          {renderStepContent()}
        </div>

        {/* Footer — sticky on mobile for one-handed import */}
        <div className="shrink-0 sticky bottom-0 z-10">
          {renderFooter()}
        </div>
      </div>
    </div>
  );
};
